/**
 * Firebase, if this deployment has any — the client half of the auth seam.
 *
 * ## Absent is a supported state, not a broken one
 *
 * With no `VITE_FIREBASE_*` values the whole module is inert: no SDK is
 * constructed, no network call is made, and `authHeaders()` answers `{}`. The
 * app then talks to qv-data exactly as it always has, with `?userId=` naming
 * the caller.
 *
 * That mirrors the server, deliberately. `QV_FIREBASE_CREDENTIALS` decides
 * whether qv-data verifies tokens; these decide whether the app has one to
 * send. Both default to off, so a developer clones the repo and everything
 * works — and a mismatch is the one thing to avoid, since a server that
 * verifies and a client that cannot sign in is an app that shows nothing.
 *
 * ## The SDK loads lazily
 *
 * `firebase/auth` is ~150KB and most of the app's value is on screen before
 * anyone signs in. Importing it at module scope would put that in the critical
 * path of a chart, for a feature that does nothing when unconfigured.
 *
 * ## What leaves here is a token, never a user
 *
 * Callers get `authHeaders()` and a uid. Nothing else in the app should hold a
 * Firebase `User`: it would spread SDK types through modules that only need to
 * know who is asking, and those are the modules that have to keep working with
 * no Firebase at all.
 */

import { passHeaders } from "./pass";
import type { Auth, User } from "firebase/auth";

const CONFIG = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY as string | undefined,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN as string | undefined,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID as string | undefined,
  appId: import.meta.env.VITE_FIREBASE_APP_ID as string | undefined,
};

/**
 * Whether this build has a Firebase project behind it.
 *
 * Read once at module load, like every other `import.meta.env` in this app —
 * Vite inlines these at build time, so they cannot change while it runs.
 *
 * `apiKey` and `projectId` are the two that matter: a config missing either
 * cannot construct an app at all, and treating a half-filled `.env.local` as
 * configured would fail at sign-in rather than at boot.
 */
export const firebaseConfigured: boolean = Boolean(CONFIG.apiKey && CONFIG.projectId);

let authPromise: Promise<Auth> | null = null;

/** The `Auth` instance, constructing it on first use. */
async function firebaseAuth(): Promise<Auth> {
  if (!firebaseConfigured) throw new Error("Firebase is not configured");
  if (!authPromise) {
    authPromise = (async () => {
      const { initializeApp, getApps } = await import("firebase/app");
      const { getAuth } = await import("firebase/auth");
      // `getApps()` first: React 18's StrictMode double-invokes effects, and
      // `initializeApp` twice throws rather than returning the existing app.
      const app = getApps()[0] ?? initializeApp(CONFIG as Record<string, string>);
      return getAuth(app);
    })();
  }
  return authPromise;
}

/**
 * Who is signed in, as it changes. Returns an unsubscribe.
 *
 * The callback fires **once with the answer** even when nobody is signed in,
 * which is what lets the app tell "still checking" from "signed out". Firebase
 * restores a session from storage asynchronously, so an app that assumed
 * signed-out at first paint would flash a sign-in screen at a returning user
 * every single load.
 *
 * Unconfigured, it reports `null` on a microtask and never again — the same
 * shape, so the caller needs no branch.
 */
export function onAuthChanged(callback: (uid: string | null) => void): () => void {
  if (!firebaseConfigured) {
    queueMicrotask(() => callback(null));
    return () => {};
  }
  let unsubscribe: (() => void) | null = null;
  let cancelled = false;
  void firebaseAuth().then(async (auth) => {
    const { onAuthStateChanged } = await import("firebase/auth");
    if (cancelled) return;
    unsubscribe = onAuthStateChanged(auth, (user: User | null) => callback(user?.uid ?? null));
  });
  return () => {
    cancelled = true;
    unsubscribe?.();
  };
}

/**
 * A fresh ID token, or `null`.
 *
 * **Not cached here.** The Firebase SDK caches internally and refreshes an hour
 * before expiry, so a copy kept in this module would be the stale one — and a
 * stale token is a 401 the user cannot explain.
 */
export async function idToken(): Promise<string | null> {
  if (!firebaseConfigured) return null;
  const auth = await firebaseAuth();
  return (await auth.currentUser?.getIdToken()) ?? null;
}

/**
 * `Authorization`, or nothing at all.
 *
 * An empty object rather than a header with an empty value: spreading `{}` into
 * a fetch's headers adds nothing, where `{ Authorization: "" }` would send a
 * malformed header the server has to decide about.
 */
export async function authHeaders(): Promise<Record<string, string>> {
  const token = await idToken();
  // The signed groups pass rides with the token: the token says who is asking,
  // the pass says what they may see, and the server needs both to skip its
  // database. See `lib/pass.ts`.
  return token ? { Authorization: `Bearer ${token}`, ...passHeaders() } : {};
}

/**
 * Which sign-in methods this project actually has switched on.
 *
 * **The client cannot ask Firebase this.** A provider that is off fails only at
 * the moment of use, with `auth/operation-not-allowed`, after a popup has opened
 * and closed — which reads as a broken app rather than an unfinished console.
 * So it is declared, and the default is the one that needs no extra setup.
 *
 * Add `google` here once Authentication → Sign-in method → Google is enabled,
 * which also needs a support email on the OAuth consent screen.
 */
export const PROVIDERS: ReadonlySet<string> = new Set(
  ((import.meta.env.VITE_FIREBASE_PROVIDERS as string | undefined) || "password")
    .split(",")
    .map((name) => name.trim().toLowerCase())
    .filter(Boolean),
);

/** Sign in with an email and password. */
export async function signInWithPassword(email: string, password: string): Promise<void> {
  const auth = await firebaseAuth();
  const { signInWithEmailAndPassword } = await import("firebase/auth");
  await signInWithEmailAndPassword(auth, email, password);
}

/**
 * Create an account with an email and password.
 *
 * Offered in the UI because a fresh project has **no users at all**, and the
 * alternative is adding the first one by hand in the Firebase console before
 * the app can be opened even once.
 */
export async function createAccountWithPassword(email: string, password: string): Promise<void> {
  const auth = await firebaseAuth();
  const { createUserWithEmailAndPassword } = await import("firebase/auth");
  await createUserWithEmailAndPassword(auth, email, password);
}

/**
 * Send a password reset link to an address.
 *
 * **It resolves whether or not that address has an account**, and the caller
 * must say the same thing either way: an answer that differs is an oracle for
 * which emails are registered here, testable one address at a time from the
 * sign-in screen with no session at all.
 *
 * Firebase's own email-enumeration protection already makes
 * `sendPasswordResetEmail` resolve on an unknown address, so swallowing
 * `auth/user-not-found` only makes a project with that setting **off** behave
 * like one with it on. Every other failure — an unreachable network, a quota,
 * a project with the provider disabled — is thrown, because those are things
 * the person in front of the screen can act on.
 */
export async function sendPasswordReset(email: string): Promise<void> {
  const auth = await firebaseAuth();
  const { sendPasswordResetEmail } = await import("firebase/auth");
  try {
    await sendPasswordResetEmail(auth, email);
  } catch (cause) {
    const message = cause instanceof Error ? cause.message : String(cause);
    if (!/user-not-found/.test(message)) throw cause;
  }
}

/** Sign in with Google. A popup, not a redirect — see `SignIn.tsx`. */
export async function signInWithGoogle(): Promise<void> {
  const auth = await firebaseAuth();
  const { GoogleAuthProvider, signInWithPopup } = await import("firebase/auth");
  await signInWithPopup(auth, new GoogleAuthProvider());
}

/** The signed-in account's email, or `null` — Firebase's own record of it. */
export async function signedInEmail(): Promise<string | null> {
  if (!firebaseConfigured) return null;
  try {
    const auth = await firebaseAuth();
    return auth.currentUser?.email ?? null;
  } catch {
    return null;
  }
}

export async function signOut(): Promise<void> {
  if (!firebaseConfigured) return;
  const auth = await firebaseAuth();
  const { signOut: fbSignOut } = await import("firebase/auth");
  await fbSignOut(auth);
}
